import { useState, type SubmitEvent } from 'react';
import { Apple, Globe2, LockKeyhole, Mail } from 'lucide-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { localizedApiError } from '../i18n/apiErrors';
import { useI18n } from '../i18n/I18nContext';

const demoPassword = 'Availora2026!';

export function LoginPage() {
  const { login, status } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('organization@availora.demo');
  const [password, setPassword] = useState(demoPassword);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  async function submit(event: SubmitEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (reason) {
      setError(localizedApiError(reason, t, 'error.loginUnavailable'));
    } finally {
      setLoading(false);
    }
  }

  function useDemo(type: 'organization' | 'professional'): void {
    setEmail(`${type}@availora.demo`);
    setPassword(demoPassword);
    setError(null);
  }

  return (
    <main className="gate login-page">
      <section className="gate-panel login-panel">
        <div className="gate-mark"><LockKeyhole size={24} /></div>
        <h1>{t('login.title')}</h1>

        <div className="provider-grid">
          <button type="button" disabled title={t('login.googlePrepared')}><Globe2 size={18} /> Google</button>
          <button type="button" disabled title={t('login.applePrepared')}><Apple size={18} /> Apple</button>
        </div>

        <div className="login-divider"><span>{t('login.orEmail')}</span></div>

        <form onSubmit={submit} className="gate-form">
          <label htmlFor="email">{t('login.email')}</label>
          <div className="input-with-icon"><Mail size={17} /><input id="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></div>
          <label htmlFor="password">{t('login.password')}</label>
          <input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          {error && <p className="form-error">{error}</p>}
          <button type="submit" disabled={loading}>{loading ? t('login.running') : t('login.title')}</button>
        </form>

        <p className="auth-switch">{t('login.noAccount')} <Link to="/register">{t('login.createAccount')}</Link></p>

        <div className="demo-logins">
          <button type="button" onClick={() => useDemo('organization')}>{t('login.organizationDemo')}</button>
          <button type="button" onClick={() => useDemo('professional')}>{t('login.professionalDemo')}</button>
        </div>
      </section>
    </main>
  );
}
